<template>
    <div
        class="flex relative flex-col bg-slate-50 py-2 px-4 gap-1 text-slate-500 rounded-sm"
        :class="{
            'opacity-50': disabled,
        }"
    >
        <div class="flex flex-row items-center justify-between">
            <span class="text-xs">{{ label }}</span>
            <font-awesome-icon widthAuto :icon="resourceIcon"></font-awesome-icon>
        </div>

        <card-loading-bars :loading="!disabled && loading" class="inset-x-4! bottom-2!" />

        <div v-if="disabled" class="flex flex-col">
            <span>-</span>
            <span>-</span>
        </div>
        <div v-else-if="!loading" class="flex flex-col">
            <div class="flex flex-row gap-1 items-center">
                <span class="font-extrabold text-xl">{{ resourcesValue || '-' }}</span>
                <span class="text-base font-normal">{{ resourcesLabel }}</span>
            </div>
            <div class="flex flex-row gap-1 items-center">
                <span class="font-extrabold text-xl">{{ messagesValue || '-' }}</span>
                <span class="text-base font-normal">messages</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    addCategoryToFetchParams,
    metricsInputValidator,
    resourceTypeIcon,
    resourceTypesOptions,
} from '~/utils/analytics';
import { computed, watch } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services/index';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { formatCount, pluralized } from '~/components/filters';
import CardLoadingBars from '../CardLoadingBars.vue';

const teamId = Spark.state.currentTeam.id;

const {
    fetchData: fetchResources,
    response: resourcesResponse,
    error: resourcesError,
    loading: resourcesLoading,
} = useVersionedRequest();

const {
    fetchData: fetchMessages,
    response: messagesResponse,
    error: messagesError,
    loading: messagesLoading,
} = useVersionedRequest();

const props = defineProps({
    category: {
        type: String,
        required: true,
    },
    resourceType: {
        type: String,
        required: true,
    },
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
});

const loading = computed(() => resourcesLoading.value || messagesLoading.value);

const resourcesValue = computed(() => {
    const value = resourcesResponse.value?.data?.data?.find((result) => result.resource_type === props.resourceType)
        ?.values[0];

    return !resourcesError.value && value !== undefined ? formatCount(value) : undefined;
});

const messagesValue = computed(() => {
    const value = messagesResponse.value?.data?.data?.find((result) => result.resource_type === props.resourceType)
        ?.values[0];

    return !messagesError.value && value !== undefined ? formatCount(value) : undefined;
});

const label = computed(
    () => resourceTypesOptions.find((type) => type.value === props.resourceType)?.label || props.resourceType
);

const disabled = computed(
    () =>
        !props.fetchParams.filters?.resource_types.includes(props.resourceType) ||
        (resourcesError.value && messagesError.value) ||
        (!loading.value && resourcesValue.value === undefined && messagesValue.value === undefined)
);

const resourceIcon = computed(() => resourceTypeIcon(props.resourceType, 'regular'));

const resourcesLabel = computed(() => {
    const labelMap = {
        user: 'agents',
        one_time_campaign: 'campaigns',
        evergreen_campaign: 'campaigns',
        automation: 'automations',
        playbook: 'playbooks',
    };

    const label = labelMap[props.resourceType] || 'resources';

    return resourcesValue.value !== undefined ? pluralized(label, resourcesValue.value) : label;
});

watch(
    () => props.fetchParams,
    async () => {
        if (!props.fetchParams.filters?.resource_types.includes(props.resourceType)) {
            return;
        }

        const params = addCategoryToFetchParams(props.fetchParams, props.category);

        fetchResources(outboundMessagesAnalyticsClient.getTotalActiveResourcesByResourceType({ teamId }, params)).catch(
            (e) => {
                console.error(e);
            }
        );

        fetchMessages(outboundMessagesAnalyticsClient.getTotalMessagesSentByResourceType({ teamId }, params)).catch(
            (e) => {
                console.error(e);
            }
        );
    },
    { immediate: true, deep: true }
);
</script>
